import React from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FiSearch, FiBookmark, FiBell, FiEdit, FiMail } from 'react-icons/fi';
import { BsThreeDots, BsInstagram, BsYoutube, BsTiktok } from 'react-icons/bs';
import { FaInstagram, FaTiktok, FaYoutube } from 'react-icons/fa';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: flex;
  align-items: center;
  height: var(--header-height);

  @media (max-width: 1024px) {
    height: var(--header-height-mobile);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1300px;
  width: 100%;
  margin: 0 auto;
  padding: 0 1.5rem;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 0.75rem;
    padding: 0 1rem;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 1024px) {
    width: 100%;
    justify-content: center;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 1024px) {
    width: 100%;
    justify-content: center;
    gap: 0.5rem;
    padding-bottom: 0.5rem;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;
  color: inherit;
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;

  @media (max-width: 768px) {
    height: 35px;
  }
`;

const LogoText = styled.h1`
  font-family: 'Source Serif 4', Georgia, serif;
  font-size: 1.5rem;
  margin: 0;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const NavItem = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #292929;
  width: 37px;
  height: 37px;

  &:hover {
    color: rgba(0, 0, 0, 0.7);
  }
`;

const NotificationIcon = styled.button`
  background: none;
  border: none;
  color: #757575;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #292929;
  }
`;

const ProfileImage = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: url('https://cdn-icons-png.flaticon.com/512/2922/2922618.png');
  background-size: cover;
  background-position: center;
  cursor: pointer;
`;

const DraftStatus = styled.span`
  color: #757575;
  font-size: 0.9rem;
`;

const PublishButton = styled.button`
  background: #1a8917;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 99px;
  font-size: 0.9rem;
  cursor: pointer;

  &:hover {
    background: #156912;
  }
`;

const MoreOptionsButton = styled.button`
  background: none;
  border: none;
  color: #757575;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 37px;
  height: 37px;

  &:hover {
    color: #292929;
  }
`;

const StoryButton = styled(Link)`
  background: #191919;
  color: white;
  border: none;
  padding: 0.5rem 1.2rem;
  border-radius: 99px;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: none;
  margin-left: 0.5rem;
  white-space: nowrap;

  &:hover {
    background: #292929;
  }

  @media (max-width: 768px) {
    padding: 0.4rem 1rem;
    font-size: 0.85rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-right: 0.5rem;
  padding-right: 0.5rem;
  border-right: 1px solid #e6e6e6;

  @media (max-width: 768px) {
    display: flex;
    border-right: none;
    margin-right: 0;
    padding-right: 0;
  }
`;

const SocialLink = styled.a`
  color: #292929;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: #1a8917;
  }
`;

const Header = ({ 
  currentPath, 
  isDraftPage,
  onPublish,
  username 
}) => {
  const navigate = useNavigate();

  const handleSearchClick = () => {
    navigate('/search');
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <HeaderLeft>
          <LogoContainer as={Link} to="/">
            <LogoImage src="/logo.png" alt="Parenting Beyond Perfection" />
            <LogoText>Parenting Beyond Perfection</LogoText>
          </LogoContainer>
        </HeaderLeft>
        <HeaderRight>
          {!isDraftPage && (
            <>
              <SocialLinks>
                <SocialLink 
                  href="https://www.instagram.com/parentingbeyond_perfection" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <FaInstagram />
                </SocialLink>
                <SocialLink 
                  href="https://www.tiktok.com/@parentingbeyondpe" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <FaTiktok />
                </SocialLink>
                <SocialLink 
                  href="https://www.youtube.com/@ParentingBeyondPerfection" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <FaYoutube />
                </SocialLink>
              </SocialLinks>
              <NavItem onClick={handleSearchClick}>
                <FiSearch size={20} />
              </NavItem>
              <NavItem as={Link} to="/bookmarks">
                <FiBookmark size={20} />
              </NavItem>
              <NavItem as={Link} to="/contact">
                <FiMail size={20} />
              </NavItem>
              <StoryButton as={Link} to="/about">Our Story</StoryButton>
            </>
          )}
          
          {isDraftPage && (
            <>
              <DraftStatus>Drafting</DraftStatus>
              <PublishButton onClick={onPublish}>
                Publish
              </PublishButton>
            </>
          )}
        </HeaderRight>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header; 